// src/components/settings/PasswordChange.tsx
import React, { use, useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { showToast } from '../../utils/toast';
import { authAPI } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';

export const PasswordChange: React.FC = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { changePassword } = useAuth();
    const [passwordStrength, setPasswordStrength] = useState(0);

    const checkPasswordStrength = (password: string) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        return Math.min(strength, 4); // 0-4 scale
    };


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Strength requirement
        if (passwordStrength < 2) { // Require at least "Fair"
            showToast('Please use a stronger password', 'error');
            return;
        }

        setIsLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
        } catch (error: any) {
            showToast(error.response?.data?.error || 'Network error', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-6">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                    <Lock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                    <p className="text-gray-600 text-sm">Update your account password</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                    </label>
                    <div className="relative">
                        <input
                            type={showCurrent ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10"
                            // required
                        />
                        <button
                            type="button"
                            onClick={() => setShowCurrent(!showCurrent)}
                            className="absolute inset-y-0 right-0 flex items-center p-3"
                        >
                            {showCurrent ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                            )}
                        </button>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password
                    </label>
                    <div className="relative">
                        <input
                            type={showNew ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => {
                                setNewPassword(e.target.value);
                                setPasswordStrength(checkPasswordStrength(e.target.value));
                            }}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 pr-10"
                            // required
                            // minLength={8}
                        />
                        <button
                            type="button"
                            onClick={() => setShowNew(!showNew)}
                            className="absolute inset-y-0 right-0 p-3 flex items-center"
                        >
                            {showNew ? (
                                <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                                <Eye className="h-5 w-5 text-gray-400" />
                            )}
                        </button>
                    </div>
                    {newPassword && (
                        <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-gray-500">Password strength:</span>
                                <span className="text-xs font-medium">
                                    {['Weak', 'Fair', 'Good', 'Strong'][passwordStrength - 1] || 'Very Weak'}
                                </span>
                            </div>
                            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className={`h-full ${passwordStrength === 1 ? 'bg-red-500' :
                                        passwordStrength === 2 ? 'bg-yellow-500' :
                                            passwordStrength === 3 ? 'bg-blue-500' :
                                                passwordStrength === 4 ? 'bg-green-500' : 'bg-gray-200'}`}
                                    style={{ width: `${(passwordStrength / 4) * 100}%` }}
                                />
                            </div>
                            <ul className="text-xs text-gray-500 mt-2 space-y-1">
                                <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
                                    ✓ At least 8 characters
                                </li>
                                <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>
                                    ✓ Uppercase letter
                                </li>
                                <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                                    ✓ Number
                                </li>
                                <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-600' : ''}>
                                    ✓ Special character
                                </li>
                            </ul>
                        </div>
                    )}
                </div>


                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                    </label>
                    <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        // required
                    />
                </div>

                <button
                    type="submit"
                    disabled={isLoading || !currentPassword || !newPassword}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {isLoading ? 'Updating...' : 'Change Password'}
                </button>
            </form>
        </div>
    );
};